import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import styled from 'styled-components';
import { useIntl } from 'react-intl';

import {
    CalculatorStepAccordion,
    MessagePropType,
    Sizes,
    useForm,
    FormComputed,
    HiddenField,
    Locales,
    Languages,
    useCalculator,
} from '@ratehub/base-ui';
import {
    RateFilterSelector,
    DEFAULT_BEST_REFINANCE_PARAMS,
    SCENARIO_TYPES,
} from '@ratehub/mtg-common';
import { INTENDED_USE } from '@ratehub/documents/mtg-journey-document/stable';


function RefinanceStepMortgageRate({
    title,
    ctaText,
    ctaDataName,
    stepNumber,
    onToggleExpand,
    onConfirmComplete,
    ...otherProps
}) {
    const form = useForm();
    const { locale } = useIntl();

    const calculatorState = useCalculator();
    const document = calculatorState.document;

    const rateFilters = useMemo(
        () => getFilterValues(toJS(document?.journey?.calculators), locale),
        [ document, locale ],
    );

    const handleCheckComplete = useCallback(
        () => form.values.selectedRate != null,
        [ form?.values?.selectedRate ],
    );

    const handleFetchRates = useCallback(rates => {
        // There is no selectedRate after a new fetch is made.
        form.setValue('selectedRate', null);
        form.setValue('bestRate', rates[0]);
    }, [ form ]);

    return (
        <Container {...otherProps}>
            <HiddenField
                name="bestRate"
            />
            <HiddenField
                name="selectedRate"
            />

            <FormComputed
                name="newMortgageRate"
                value={convertMortgageRate}
            />

            <CalculatorStepAccordion
                title={title}
                ctaText={ctaText}
                ctaDataName={ctaDataName}
                value="newMortgageRate"
                valueFormat="percent2"
                stepNumber={stepNumber}
                showBadge={true}
                canBeDisabled={true}
                onCheckComplete={handleCheckComplete}
                hasCompleteButton={false}
                onConfirmComplete={(stepNumber, formValues) => onConfirmComplete?.({ stepNumber, formValues, document })}
                onToggleExpand={onToggleExpand}
            >
                {step => (
                    <div className="stepContent">
                        <RateFilterSelector
                            initialFilters={rateFilters}
                            onSelectRate={selectedRate => {
                                form.setValue('selectedRate', selectedRate);
                                step.confirmComplete();
                            }}
                            onFetchRates={handleFetchRates}
                            excludedFilters={[]}
                        />
                    </div>
                )}
            </CalculatorStepAccordion>
        </Container>
    );
}

function convertMortgageRate(values) {
    return (
        (values.selectedRate && values.selectedRate.value) / 100 || undefined
    );
}

function getFilterValues(savedCalculatorData, locale) {
    if (!savedCalculatorData) {
        return {
            ...DEFAULT_BEST_REFINANCE_PARAMS,
            scenario: SCENARIO_TYPES.REFINANCE,

            intendedUse: INTENDED_USE.OWNER_OCCUPIED,
        };
    }

    const mortgage = savedCalculatorData?.mortgage;
    const property = savedCalculatorData?.property;
    const propertyAddress = property?.address;
    const intendedUse = savedCalculatorData?.intendedUse ?? INTENDED_USE.OWNER_OCCUPIED;

    return {
        ...DEFAULT_BEST_REFINANCE_PARAMS,
        scenario: SCENARIO_TYPES.REFINANCE,
        language: locale === Locales.FRENCH ? Languages.FRENCH : Languages.ENGLISH,

        intendedUse: intendedUse,
        term: mortgage?.term ?? DEFAULT_BEST_REFINANCE_PARAMS.term,
        type: mortgage?.type ?? DEFAULT_BEST_REFINANCE_PARAMS.type,
        amortization: mortgage?.amortization ? mortgage?.amortization / 12 : DEFAULT_BEST_REFINANCE_PARAMS.amortization,
        city: getCityObjectFromCityProvincePair(propertyAddress) ?? DEFAULT_BEST_REFINANCE_PARAMS.city,
    };
}

function getCityObjectFromCityProvincePair({ city, province } = {}) {
    if (!city || !province) {
        return null;
    }

    return { name: city, province: { code: province } };
}

RefinanceStepMortgageRate.propTypes = {
    title: MessagePropType.isRequired,
    ctaText: MessagePropType.isRequired,
    ctaDataName: PropTypes.string.isRequired,
    stepNumber: PropTypes.number.isRequired,

    onConfirmComplete: PropTypes.func,
    onToggleExpand: PropTypes.func,
};

RefinanceStepMortgageRate.defaultProps = {
    onConfirmComplete: undefined,
    onToggleExpand: undefined,
};

const Container = styled.section`
    .stepContent {
        padding: ${Sizes.SPACING.ONE} 0 0;

        > .contactBroker {
            margin: ${Sizes.SPACING.TWO} auto 0;
        }
    }
`;

export default observer(RefinanceStepMortgageRate);
